<template>
  <div :class="['type-' + resource.type]">
    <author-toolbar
      v-if="loggedInUser && loggedInUser.capabilities.includes('publish_posts')"
      :post="resource"
    />

    <div class="row">
      <div class="columns small-12 medium-9 large-8">
        <section v-if="resource.videos" class="section section-player">
          <div v-if="hasPermissions" class="flex-video">
            <wistia-video
              :title="resource.name"
              :wistia-id="resource.videos[0].name"
              embed="iframe"
            />
          </div>
          <template v-else>
            <nuxt-link
              :to="localePath('/plans/')"
              title="Upgrade or login to watch video"
              class="upgrade-overlay"
            >
              <responsive-image :image="resource.media_thumbnail || resource.media[0]" alt="" />
            </nuxt-link>
            <div class="callout">
              <span>
                <nuxt-link :to="localePath('/login/')">Login</nuxt-link>
                to watch video
              </span>
            </div>
          </template>
        </section>

        <header>
          <div :class="['text-' + resource.type, 'subheader']">Video</div>
          <h1>{{ resource.name }}</h1>

          <template v-if="resource.breadcrumbs">
            <t-breadcrumbs :items="resource.breadcrumbs" class="tw-mt-1" />
          </template>
        </header>

        <div class="section section-actions flex flex-wrap align-middle">
          <action-buttons
            class="pull-right"
            :resource-id="resource.id"
            :event-params="resource.event_params"
          />
        </div>

        <div class="callout callout-details">
          <publisher-link v-if="resource.publisher" :publisher="resource.publisher" />
          <div>
            <template v-if="resource.duration">
              <strong>{{ resource.duration }} {{ pluralise('min', resource.duration) }}</strong>
              <span>|</span>
            </template>
            <template v-if="resource.year_levels_range">
              {{ $t('grade_label') }}s:
              <strong>{{ resource.year_levels_range }}</strong>
            </template>
          </div>
        </div>

        <section class="section section-content tw-prose tw-max-w-none">
          <p v-if="resource.excerpt"><strong v-html="resource.excerpt"></strong></p>
          <div v-if="resource.content" class="description" v-html="resource.content"></div>
        </section>

        <section
          v-if="resource.curriculums && resource.curriculums.length > 0"
          class="section section-curriculum"
        >
          <h3>Curriculum</h3>
          <curriculum-list :curriculums="resource.curriculums" />
        </section>

        <section v-if="resource.author" class="section section-author">
          <h3>Author</h3>

          <div class="flex">
            <figure v-if="resource.author && resource.author.photo">
              <img
                :src="resource.author.photo"
                class="circle"
                :alt="'Photo of ' + resource.author.display_name"
                height="42"
                width="42"
              />
            </figure>
            <span>{{ resource.author.display_name }}</span>
          </div>
        </section>

        <section v-if="resource.publisher" class="section section-publisher">
          <div class="flex">
            <figure v-if="resource.publisher && resource.publisher.logo">
              <img
                :src="resource.publisher.logo_square"
                class="circle"
                height="42"
                width="42"
                :alt="resource.publisher.name"
              />
            </figure>
            <div>
              <h3>{{ resource.publisher.name }}</h3>
              <p>{{ resource.publisher.excerpt }}</p>
            </div>
          </div>
        </section>

        <tag-list
          :file-formats="resource.file_formats"
          :tags="resource.tags"
          :year-levels="resource.year_levels"
          :country="resource.country"
        />
      </div>

      <aside class="columns small-12 medium-3 large-4">
        <div>
          <h3 class="text-center">Related videos</h3>
          <resources-list
            align="center"
            :load-more="true"
            :params="{ id: resource.id, type: 'video' }"
            :col-count="2"
            list-type="related"
          />
        </div>
      </aside>
    </div>

    <comment-section
      class="section-comments"
      :resource-id="resource.id"
      :comment-count="resource.comments"
      :resource-updates="resource.updates"
      :event-params="resource.event_params"
    />

    <resources-list
      align="center"
      :load-more="true"
      :params="{ id: resource.id }"
      title="More time-saving resources!"
      list-type="related"
    />
    <country-switcher :link="resource.link" :linked="resource.linked" :country="resource.country" />
  </div>
</template>

<script>
/* A single teaching resource
 *
 * @parent:
 * @requests: /public/v1/resource
 * @events:
 * @props:
 * @components: ActionButtons, AuthorToolbar, AddToCalendar, TagList, CommentSection, CurriculumList, DownloadButton, DownloadLink, ImageGallery, ResourcesList
 * @methods:
 */

import { mapGetters } from 'vuex';

import TBreadcrumbs from '~/components/atoms/t-breadcrumbs.vue';
import ActionButtons from '~/components/resource/ActionButtons.vue';
import TagList from '~/components/resource/TagList.vue';
import CommentSection from '~/components/comment/CommentSection.vue';
import ResourcesList from '~/components/resource/ResourcesList.vue';
import CurriculumList from '~/components/resource/CurriculumList.vue';
import ResponsiveImage from '~/components/ResponsiveImage.vue';
import PublisherLink from '~/components/PublisherLink.vue';
import WistiaVideo from '~/components/WistiaVideo.vue';
import CountrySwitcher from '~/components/CountrySwitcher.vue';
import { pluralise } from '~/utils/formatStrings';

export default {
  name: 'VideoResource',
  components: {
    TBreadcrumbs,
    ActionButtons,
    AuthorToolbar: () => import('~/components/AuthorToolbar.vue'),
    TagList,
    CommentSection,
    ResourcesList,
    CurriculumList,
    ResponsiveImage,
    PublisherLink,
    WistiaVideo,
    CountrySwitcher,
  },
  validate({ _params }) {
    return true;
    //return /\s/g.test(params.slug);
  },
  async asyncData({ app, store, route, params, error }) {
    const data = { types: ['video'] };

    try {
      const resource = await store.dispatch('loadResource', {
        id: params.slug,
        types: data.types,
        link: route.path,
        country: app.i18n.locale,
      });

      data.title = resource.name || resource.meta_title;
    } catch (err) {
      // No page matching this route
      error({ statusCode: 404, message: err.message, path: route.path });
    }

    return data;
  },
  data() {
    return {
      title: 'Resource',
      description: 'A teaching resource from Teach Starter',
    };
  },
  jsonld() {
    return this.$jsonSchemaResource(this.resource);
  },
  head() {
    return {
      title: this.title,
      meta: this.$metaPropertiesResource(this.resource),
      script: [
        {
          hid: 'wistia',
          src: 'https://fast.wistia.com/assets/external/E-v1.js',
          defer: true,
          callback: () => {
            if (window._wq) {
              window._wq.push({
                id: this.resource.wistia_id,
              });
            }
          },
        },
      ],
      link: this.$hrefLangsResource(
        this.resource.linked,
        this.resource.link,
        this.resource.country
      ),
    };
  },
  computed: {
    ...mapGetters(['loggedInUser', 'resource']),
    hasPermissions() {
      return (
        (this.loggedInUser && this.resource.is_free) ||
        (this.loggedInUser && this.loggedInUser.capabilities.includes('access_product_videos'))
      );
    },
  },
  methods: {
    pluralise,
  },
};
</script>

<style scoped lang="scss">
@import '~/assets/scss/sections/_resource-page.scss';

.flex-video {
  min-height: 18.75rem;
}

.section-player {
  padding-bottom: 0;
}

.upgrade-overlay {
  filter: grayscale(0.5);
}
</style>
